/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import Reveal from "./Reveal";
import { LANDING } from "@/constants/testIds";

const STEPS = [
  {
    num: "01",
    title: "You place your Utsah order",
    desc: "Choose your collection, quantity, and customisation. The Green Gift Initiative is included automatically — no extra step.",
  },
  {
    num: "02",
    title: "Choose your reforestation project",
    desc: "Pick any active project across India — each one supporting local communities and biodiversity.",
  },
  {
    num: "03",
    title: "We plant the trees",
    desc: "One hamper. One tree. Planted through our Grow-Trees partnership, tracked and verified end-to-end.",
  },
  {
    num: "04",
    title: "Certificate in every box",
    desc: "Every recipient finds a certificate — with your company's name and the reforestation project they've contributed to.",
  },
];

export default function HowItWorks() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  // Track which step is centered in the scroller (mobile carousel only)
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const nodes = Array.from(track.querySelectorAll(".how-step"));
    if (!nodes.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.55) {
            const idx = Number(e.target.getAttribute("data-idx"));
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { root: track, threshold: [0.55, 0.75], rootMargin: "0px" }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  const scrollToStep = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const node = track.querySelector(`.how-step[data-idx="${idx}"]`);
    if (node) node.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  return (
    <section className="section how">
      <div className="section-inner">
        <div className="how-header">
          <Reveal className="eyebrow cream">How It Works</Reveal>
          <Reveal as="h2" delay={1} className="section-headline cream">
            Simple. And it <em>actually</em> happens.
          </Reveal>
        </div>

        <div className="how-steps-wrap">
          <div className="how-steps" ref={trackRef} data-testid="how-steps-track">
            {STEPS.map((s, i) => (
              <Reveal
                key={s.num}
                delay={i > 3 ? 3 : i}
                className="how-step"
                data-idx={i}
                data-testid={LANDING.howStep(i + 1)}
              >
                <div className="how-step-num">{s.num}</div>
                <div className="how-step-title">{s.title}</div>
                <div className="how-step-desc">{s.desc}</div>
              </Reveal>
            ))}
          </div>

          {/* Dot indicators — visible only in mobile carousel mode */}
          <div className="how-dots" data-testid="how-dots" aria-hidden>
            {STEPS.map((s, i) => (
              <button
                key={s.num}
                type="button"
                className={`how-dot ${active === i ? "active" : ""}`.trim()}
                aria-label={`Go to step ${i + 1}`}
                data-testid={`how-dot-${i + 1}`}
                onClick={() => scrollToStep(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
